// src/components/onboarding/steps/Step2BusinessBasics.tsx
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { OnboardingStepProps } from '@/types/onboarding';
import { useBranding } from '@/contexts/BrandingContext';

const Step2BusinessBasics: React.FC<OnboardingStepProps> = ({ data, updateData, goToNextStep, goToPrevStep }) => {
  const { branding } = useBranding();
  const [errors, setErrors] = useState<{ businessName?: string; aiGreetingMessage?: string }>({});

  // Update greeting default when business name changes
  useEffect(() => {
    if (data.businessName && !data.aiGreetingMessage) { // Only update if greeting is empty
      updateData({
        aiGreetingMessage: branding?.customCopy?.aiGreeting || `Hi, thank you for calling, this is ${branding?.customCopy?.aiName || 'Jade'}, the AI assistant. How can I help?`
      });
    }
    // Basic validation for business name change
    if (data.businessName) {
        setErrors(prev => ({ ...prev, businessName: undefined }));
    }
  }, [data.businessName, data.aiGreetingMessage, updateData]);

   // Basic validation for greeting change
  useEffect(() => {
    if (data.aiGreetingMessage) {
        setErrors(prev => ({ ...prev, aiGreetingMessage: undefined }));
    }
  }, [data.aiGreetingMessage]);


  const validateAndProceed = () => {
    const newErrors: { businessName?: string; aiGreetingMessage?: string } = {};
    if (!data.businessName.trim()) {
      newErrors.businessName = 'Business name is required.';
    }
    if (!data.aiGreetingMessage.trim()) {
      newErrors.aiGreetingMessage = 'Greeting message is required.';
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      goToNextStep();
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-gray-100">Tell us about your business.</h2>

      <div className="space-y-6">
        <div>
          <Label htmlFor="businessName" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            What's the name of your business? <span className="text-red-500">*</span>
          </Label>
          <Input
            id="businessName"
            type="text"
            value={data.businessName}
            onChange={(e) => updateData({ businessName: e.target.value })}
            placeholder="e.g., Acme Services"
            className={`mt-1 ${errors.businessName ? 'border-red-500' : ''}`}
            required
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your AI will use this name when introducing itself.</p>
          {errors.businessName && <p className="text-xs text-red-600 mt-1">{errors.businessName}</p>}
        </div>

        <div>
          <Label htmlFor="aiGreetingMessage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            How should your AI greet callers? <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="aiGreetingMessage"
            value={data.aiGreetingMessage}
            onChange={(e) => updateData({ aiGreetingMessage: e.target.value })}
            placeholder="Example: 'Hi, thank you for calling [Business Name], this is Jade, the AI assistant. How can I help?'"
            className={`mt-1 ${errors.aiGreetingMessage ? 'border-red-500' : ''}`}
            rows={4}
            required
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Tip: Keep it friendly and clear. You can edit this later.
          </p>
           {errors.aiGreetingMessage && <p className="text-xs text-red-600 mt-1">{errors.aiGreetingMessage}</p>}
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={goToPrevStep} disabled={!goToPrevStep}>
          Back
        </Button>
        <Button onClick={validateAndProceed}>Next</Button>
      </div>
    </div>
  );
};

export default Step2BusinessBasics;